import firebase from "firebase/app";
import "firebase/storage";
import { getIllustrationRegions } from "./regionsApi";
import { getAppIds } from "./appIdsApi";
import axios from "axios";
import FormData from "form-data";


export async function getIllustrationNameComponentCategories() {
  var db = firebase.firestore();
  return await db.collection("illustrationNameComponentCategories").get();
}

export async function getIllustrationDetails(illustrationUid) {
  if (illustrationUid) {
    var db = firebase.firestore();
    var doc = await db.collection("illustrations").doc(illustrationUid).get();
    var regionalIllustrationsArray = await getRegionalIllustrations(
      illustrationUid
    );

    if (doc.exists) {
      return {
        ...doc.data(),
        id: doc.id,
        created_at: doc.get("default.created_at").toDate(),
        updated_at: doc.get("default.last_updated_at").toDate(),
        regional_illustrations: regionalIllustrationsArray,
      };
    } else {
      throw new Error("Illustration with this ID doesnt exist");
    }
  } else {
    throw new Error("Invalid Illustration ID");
  }
}

export async function createDefaultIllustration(
  name,
  illustrationFile,
  userId,
  darkroomPublicUrl,
  variantSync = true,
  darkroomPublicUrlDark = null,
  illustrationFileDark = null
) {
  var combinedName = getCombinedName(name);
  // Check if illustration name is unique
  await isIllustrationNameUnique(combinedName);
  // Create illustration record on firebase
  var recordId = await createDefaultIllustrationRecordOnDb(
    name,
    combinedName,
    userId,
    darkroomPublicUrl,
    variantSync,
    darkroomPublicUrlDark
  );
  // Upload light SVG to firebase storage and save img_url
  var fileUrl = await uploadIllustrationFile(
    combinedName,
    "default",
    illustrationFile
  );
  await addFileUrlForDefaultIllustration(recordId, userId, fileUrl);
  // Upload dark SVG to firebase storage and save img_url_dark
  if (!variantSync && illustrationFileDark) {
    var fileUrlDark = await uploadIllustrationFile(
      combinedName,
      "dark",
      illustrationFileDark
    );
    await addDarkFileUrlForDefaultIllustration(recordId, userId, fileUrlDark);
  }
  return recordId;
}

export async function updateDefaultIllustration(
  userId,
  illustrationId,
  illustrationFile,
  combinedName
) {
  // upload image to firebase storage
  var fileUrl = await uploadIllustrationFile(
    combinedName,
    "default",
    illustrationFile
  );
  // Update url of uploaded image to created record
  return await addFileUrlForDefaultIllustration(
    illustrationId,
    userId,
    fileUrl
  );
}

// Sync path: one file applied to both light and dark variants
export async function updateDefaultIllustrationSynced(
  userId,
  illustrationId,
  illustrationFile,
  combinedName,
  darkroomPublicUrl
) {
  const fileUrl = await uploadIllustrationFile(combinedName, "default", illustrationFile);
  const db = firebase.firestore();
  return await db.collection("illustrations").doc(illustrationId).update({
    variant_sync: true,
    "default.img_url": fileUrl,
    "default.img_url_dark": fileUrl,
    "default.darkroom_public_url": darkroomPublicUrl,
    "default.darkroom_public_url_dark": darkroomPublicUrl,
    "default.last_updated_at": firebase.firestore.FieldValue.serverTimestamp(),
    "default.last_updated_by": userId,
  });
}

// Light-only path: only light variant updated, variant_sync written as false
export async function updateDefaultIllustrationLight(
  userId,
  illustrationId,
  illustrationFile,
  combinedName,
  darkroomPublicUrl
) {
  const fileUrl = await uploadIllustrationFile(combinedName, "default", illustrationFile);
  const db = firebase.firestore();
  return await db.collection("illustrations").doc(illustrationId).update({
    variant_sync: false,
    "default.img_url": fileUrl,
    "default.darkroom_public_url": darkroomPublicUrl,
    "default.last_updated_at": firebase.firestore.FieldValue.serverTimestamp(),
    "default.last_updated_by": userId,
  });
}

// Dark-only path: only dark variant updated, variant_sync written as false
export async function updateDefaultIllustrationDark(
  userId,
  illustrationId,
  darkFile,
  combinedName,
  darkroomPublicUrlDark
) {
  const fileUrlDark = await uploadIllustrationFile(combinedName, "dark", darkFile);
  const db = firebase.firestore();
  return await db.collection("illustrations").doc(illustrationId).update({
    variant_sync: false,
    "default.img_url_dark": fileUrlDark,
    "default.darkroom_public_url_dark": darkroomPublicUrlDark,
    "default.last_updated_at": firebase.firestore.FieldValue.serverTimestamp(),
    "default.last_updated_by": userId,
  });
}

export async function resetRegionalIllustrationToDefault(
  illustrationUid,
  regionalIllustrationUid,
  userId
) {
  var db = firebase.firestore();
  var regionalIllustrationsCollectionsRef = db
    .collection("illustrations")
    .doc(illustrationUid)
    .collection("regional_illustrations");

  return await regionalIllustrationsCollectionsRef
    .doc(regionalIllustrationUid)
    .update({
      same_as_default: true,
      img_url: "",
      last_updated_at: firebase.firestore.FieldValue.serverTimestamp(),
      last_updated_by: userId,
    });
}

export async function updateRegionalIllustration(
  illustrationUid,
  regionalIllustrationUid,
  userId,
  combinedName,
  regionIdentifier,
  illustrationFile
) {
  // upload image to firebase storage
  var fileUrl = await uploadIllustrationFile(
    combinedName,
    regionIdentifier,
    illustrationFile
  );
  // update url of uploaded image for the regional illustration
  return await updateFileUrlForRegionalIllustration(
    illustrationUid,
    regionalIllustrationUid,
    fileUrl,
    userId
  );
}

/**
 * Retrieve storage urls from Darkroom. Gets the upload_url, public_url and download_url
 * from darkroom
 *
 * @param {String} fileName - Name of the asset
 * @return {Promise} Promise which contains the response data from darkroom
 */
export async function getDarkroomStorageUrls(fileName) {
  var data = JSON.stringify({ file_name: `${fileName}.png` });

  var config = {
    method: "POST",
    url: process.env.REACT_APP_DARKROOM_API_URL,
    headers: {
      "Content-Type": "application/json",
      "X-DARK-ROOM-APP-ID": process.env.REACT_APP_DARKROOM_APP_ID,
    },
    data: data,
  };

  return await axios(config);
}

/**
 * Calls ImageUpload cloud function which uploads the image to s3 bucket
 *
 * @param {String} uploadUrl - Upload url of image retrieved from darkroom
 * @param {Blob} file - The raster (png) illustration file to upload
 * @return {Promise} - Promise which succeeds once the request is successful
 */
export async function uploadFileToBucket(uploadUrl, file, token) {
  const data = new FormData();
  data.append("uploadUrl", uploadUrl);
  if (file) {
    data.append("file", file);
  }
  let config = {
    method: "post",
    // url: process.env.REACT_APP_IMAGE_UPLOAD_CLOUD_FUNCTION_URL,
    url: `${process.env.REACT_APP_BFF_BASE_URL}/api/coffer/cors-proxy`,
    headers: {
      Authorization: `bearer ${token}`,
    },
    data: data,
  };
  return await axios(config);
}

export async function checkVpnConnection() {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 2000);

  try {
    // no-cors: we only need to know if the internal ping is reachable (VPN on).
    // axios/CORS would fail from localhost even when VPN is connected.
    await fetch(process.env.REACT_APP_VPN_CHECK_URL, {
      method: "GET",
      mode: "no-cors",
      cache: "no-store",
      signal: controller.signal,
    });
    return true;
  } catch (err) {
    console.error("[checkVpnConnection] VPN check failed:", {
      message: err.message,
      name: err.name,
    });
    return false;
  } finally {
    clearTimeout(timeoutId);
  }
}

async function updateFileUrlForRegionalIllustration(
  illustrationUid,
  regionalIllustrationUid,
  fileUrl,
  userId
) {
  var db = firebase.firestore();
  var regionalIllustrationsCollectionsRef = db
    .collection("illustrations")
    .doc(illustrationUid)
    .collection("regional_illustrations");

  return await regionalIllustrationsCollectionsRef
    .doc(regionalIllustrationUid)
    .update({
      same_as_default: false,
      img_url: fileUrl,
      last_updated_at: firebase.firestore.FieldValue.serverTimestamp(),
      last_updated_by: userId,
    });
}

/* Throws if an illustration with the same combined name already exists.
Exported so the create flow can fail fast before uploading to Darkroom. */
export async function isIllustrationNameUnique(combinedName) {
  // check on firebase if illustration with same name already exists
  var db = firebase.firestore();
  var querySnapshot = await db
    .collection("illustrations")
    .where("combined_name", "==", combinedName)
    .limit(1)
    .get();
  if (querySnapshot.size > 0) {
    throw new Error("Illustration with the same name already exists");
  } else {
    return;
  }
}

async function createDefaultIllustrationRecordOnDb(
  name,
  combinedName,
  userId,
  darkroomPublicUrl,
  variantSync = true,
  darkroomPublicUrlDark = null
) {
  var db = firebase.firestore();
  var illustrationsCollectionRef = db.collection("illustrations");

  var defaultIllustrationObj = {
    name: name,
    combined_name: combinedName,
    variant_sync: variantSync,
    default: {
      created_at: firebase.firestore.FieldValue.serverTimestamp(),
      created_by: userId,
      last_updated_at: firebase.firestore.FieldValue.serverTimestamp(),
      last_updated_by: userId,
      ...(darkroomPublicUrl
        ? { darkroom_public_url: darkroomPublicUrl }
        : {}),
      ...(!variantSync && darkroomPublicUrlDark
        ? { darkroom_public_url_dark: darkroomPublicUrlDark }
        : {}),
    },
  };

  const docRef = await illustrationsCollectionRef.add(defaultIllustrationObj);
  await addRegionalDataForIllustration(
    illustrationsCollectionRef,
    docRef.id,
    userId
  );
  return docRef.id;
}

async function getRegionalIllustrationsArray(userId) {
  var regionsArray = await getIllustrationRegions();
  var regionalIllustrationsArray = [];
  regionsArray.forEach((regionDoc) => {
    regionalIllustrationsArray.push({
      region_id: regionDoc.id,
      img_url: "",
      same_as_default: true,
      created_by: userId,
      last_updated_by: userId,
      created_at: firebase.firestore.FieldValue.serverTimestamp(),
      last_updated_at: firebase.firestore.FieldValue.serverTimestamp(),
    });
  });

  return regionalIllustrationsArray;
}

/* Updates default illustration document's image url field*/
async function addFileUrlForDefaultIllustration(recordId, userId, fileUrl) {
  var db = firebase.firestore();
  var illustrationsCollectionRef = db.collection("illustrations");

  return await illustrationsCollectionRef.doc(recordId).update({
    "default.img_url": fileUrl,
    "default.last_updated_at": firebase.firestore.FieldValue.serverTimestamp(),
    "default.last_updated_by": userId,
  });
}

async function addDarkFileUrlForDefaultIllustration(recordId, userId, fileUrlDark) {
  var db = firebase.firestore();
  var illustrationsCollectionRef = db.collection("illustrations");

  return await illustrationsCollectionRef.doc(recordId).update({
    "default.img_url_dark": fileUrlDark,
    "default.last_updated_at": firebase.firestore.FieldValue.serverTimestamp(),
    "default.last_updated_by": userId,
  });
}

/**
 * Updates the default illustration document with the Darkroom public URL.
 * @param {String} illustrationId - Firestore document ID of the illustration
 * @param {String} userId - User ID (last_updated_by)
 * @param {String} darkroomPublicUrl - The public URL from Darkroom for this asset
 */
export async function updateDarkroomUrlForDefaultIllustration(
  illustrationId,
  userId,
  darkroomPublicUrl
) {
  var db = firebase.firestore();
  return await db.collection("illustrations").doc(illustrationId).update({
    "default.darkroom_public_url": darkroomPublicUrl,
    "default.last_updated_at": firebase.firestore.FieldValue.serverTimestamp(),
    "default.last_updated_by": userId,
  });
}

/* Uploads an illustration file to storage and returns url of the uploaded file */
async function uploadIllustrationFile(
  combinedIllustrationName,
  identifier,
  file
) {
  // Identifier could be default or region identifier or event identifier
  const storage = firebase.storage();
  var illustrationsRootFolderRef = storage.ref("coffer/illustrations");
  var illustrationImageRef = illustrationsRootFolderRef.child(
    `${combinedIllustrationName}/${identifier}_${combinedIllustrationName}.svg`
  );
  const maxAge = 90 * 24 * 60 * 60; // days * hours * minutes * seconds
  var metadata = {
    cacheControl: `max-age=${maxAge}`,
  };
  await illustrationImageRef.put(file, metadata);
  const url = await illustrationImageRef.getDownloadURL();
  return url;
}

/* Retrieves the regions collection and returns a map of the regions */
async function getIllustrationRegionsMap() {
  var regionsMap = new Map();
  var regions = await getIllustrationRegions();
  regions.forEach((region) => {
    regionsMap.set(region.id, region.data());
  });
  return regionsMap;
}

/* Adds data to regional_illustrations sub-collection. Used only when a new default 
illustration is created */
async function addRegionalDataForIllustration(
  illustrationsCollectionRef,
  illustrationUid,
  userId
) {
  var regionalIllustrationsCollectionsRef = illustrationsCollectionRef
    .doc(illustrationUid)
    .collection("regional_illustrations");
  var regionalIllustrationsArray = await getRegionalIllustrationsArray(userId);
  await Promise.all(
    regionalIllustrationsArray.map((regionalIllustrationObj) => {
      return regionalIllustrationsCollectionsRef.add(regionalIllustrationObj);
    })
  );
}

/* Retrieves all the documents from the regional_illustrations sub-collection given 
the default illustration document id */
async function getRegionalIllustrations(illustrationUid) {
  var regionsMap = await getIllustrationRegionsMap();
  var db = firebase.firestore();
  var regionalIllustrations = await db
    .collection("illustrations")
    .doc(illustrationUid)
    .collection("regional_illustrations")
    .get();
  return replaceRegionIdWithRegionData(regionalIllustrations, regionsMap);
}

/* Returns combined illustration name given the Name object */
export function getCombinedName(name) {
  return `${name.product_category}_${name.illustration_category}_${name.illustration_name}`;
}

/* Returns an array of regional illustration objects by replacing the region_id with
the Region object */
function replaceRegionIdWithRegionData(regionalIllustrations, regionsMap) {
  var regionalIllustrationsArray = [];
  regionalIllustrations.forEach((regionalIllustration) =>
    regionalIllustrationsArray.push({
      ...regionalIllustration.data(),
      id: regionalIllustration.id,
      region_name: regionsMap.get(regionalIllustration.data().region_id).name,
      region_identifier: regionsMap.get(regionalIllustration.data().region_id)
        .identifier,
    })
  );
  return regionalIllustrationsArray.sort((a, b) =>
    a.region_name.localeCompare(b.region_name)
  );
}

/**
 * Fetches the current BCS mobile config for the given asset via the BFF
 *
 * @param {String} configKey - Uppercased combined name of the asset
 * @param {String} token - Logged-in user's Google id token
 * @return {Promise} Promise resolving to the BCS mobile config response
 */
export async function getBcsMobileConfig(configKey, token, bffBaseUrl = process.env.REACT_APP_BFF_BASE_URL, bcsEnv) {
  let config = {
    method: "get",
    url: `${bffBaseUrl}/api/coffer/bcs/portal/v1/mobile-configs/${configKey}`,
    headers: {
      Authorization: `bearer ${token}`,
      ...(bcsEnv && { 'X-Coffer-BCS-Env': bcsEnv }),
    },
    timeout: 15000
  };
  return await axios(config);
}

/**
 * Updates BCS app_id values for an asset via the BFF
 *
 * @param {Array} keys - Array of {configKey, configValue, dimensions} entries
 * @param {String} token - Logged-in user's Google id token
 * @return {Promise} Promise resolving when BCS accepts the update
 */
export async function updateBcsAppIdValues(keys, token, bffBaseUrl = process.env.REACT_APP_BFF_BASE_URL, bcsEnv) {
  let config = {
    method: "put",
    url: `${bffBaseUrl}/api/coffer/bcs/internal/v2/configs`,
    headers: {
      Authorization: `bearer ${token}`,
      "Content-Type": "application/json",
      ...(bcsEnv && { 'X-Coffer-BCS-Env': bcsEnv }),
    },
    data: JSON.stringify({ username: "coffer", keys }),
    timeout: 15000
  };
  return await axios(config);
}

/**
 * Currently unused: kept for the upcoming condition-removal (bin) feature.
 * Deletes the app_id-scoped BCS condition for the given asset via the BFF.
 *
 * @param {String} configKey - Uppercased combined name of the asset
 * @param {String} appId - The application id whose condition to remove
 * @param {String} token - Logged-in user's Google id token
 * @return {Promise} Promise resolving when BCS removes the condition
 */
export async function deleteBcsAppIdValue(configKey, appId, token) {
  // Without app-id this same endpoint deletes the asset's GLOBAL value.
  if (!appId) {
    throw new Error("appId is required — refusing to call the global delete endpoint");
  }
  let config = {
    method: "delete",
    url: `${process.env.REACT_APP_BFF_BASE_URL}/api/coffer/bcs/internal/v1/mobile-configs/${configKey}/default?app-id=${encodeURIComponent(appId)}`,
    headers: {
      Authorization: `bearer ${token}`,
    },
    timeout: 15000
  };
  return await axios(config);
}

export async function syncBcsIllustrationUrls(configKey, lightUrl, darkUrl, token, bffBaseUrl, bcsEnv) {
  const response = await getBcsMobileConfig(configKey, token, bffBaseUrl, bcsEnv);
  const conditions = response.data.data?.global?.conditions ?? [];
  const keys = conditions
    .filter((condition) => {
      try { return !!JSON.parse(condition.value)?.illustration_url; }
      catch { return false; }
    })
    .map((condition) => ({
      configKey,
      configValue: JSON.stringify({ illustration_url: lightUrl, illustration_url_dark: darkUrl }),
      dimensions: { app_id: condition.applicationId, platform: "mobile" },
    }));
  if (keys.length > 0) {
    await updateBcsAppIdValues(keys, token, bffBaseUrl, bcsEnv);
  }
}

function getBffUrlForEnv(env) {
  if (env === 'prod') return process.env.REACT_APP_BFF_BASE_URL;
  if (env === 'qa') {
    return (
      process.env.REACT_APP_QA_BFF_BASE_URL ||
      process.env.REACT_APP_INT_BFF_BASE_URL ||
      process.env.REACT_APP_BFF_BASE_URL
    );
  }
  return process.env.REACT_APP_INT_BFF_BASE_URL || process.env.REACT_APP_BFF_BASE_URL;
}

/**
 * On asset creation: enables all app_ids for all environments in BCS with the
 * new Darkroom URL. Each environment is activated in parallel.
 *
 * @param {String} configKey - Uppercased combined name of the asset
 * @param {String} darkroomUrl - The public Darkroom URL for this asset
 * @param {String} token - Logged-in user's Google id token
 */
export async function activateBcsForAllAppIds(configKey, darkroomUrl, darkroomUrlDark, token) {
  const IS_INTEGRATION = process.env.REACT_APP_ENVIRONMENT === 'integration';
  const IS_PRODUCTION = process.env.REACT_APP_ENVIRONMENT === 'prod';

  const envsToActivate = IS_PRODUCTION
    ? [
        { appIdKey: 'integration', bcsEnv: 'integration', bffBaseUrl: getBffUrlForEnv('integration') },
        { appIdKey: 'qa',          bcsEnv: 'qa',          bffBaseUrl: getBffUrlForEnv('qa') },
        { appIdKey: 'prod',        bcsEnv: undefined,     bffBaseUrl: getBffUrlForEnv('prod') },
      ]
    : IS_INTEGRATION
    ? [
        { appIdKey: 'integration', bcsEnv: 'integration', bffBaseUrl: getBffUrlForEnv('integration') },
        { appIdKey: 'qa',          bcsEnv: 'qa',          bffBaseUrl: getBffUrlForEnv('qa') },
      ]
    : [
        { appIdKey: 'prod', bcsEnv: undefined, bffBaseUrl: undefined },
      ];

  const appIdsMap = await getAppIds();

  await Promise.all(
    envsToActivate.map(({ appIdKey, bcsEnv, bffBaseUrl }) => {
      const appIds =
        appIdKey === 'prod'
          ? (appIdsMap.prod || appIdsMap.production || [])
          : (appIdsMap[appIdKey] || []);

      if (appIds.length === 0) return Promise.resolve();

      const keys = appIds.map((appId) => ({
        configKey,
        configValue: JSON.stringify({ illustration_url: darkroomUrl, illustration_url_dark: darkroomUrlDark }),
        dimensions: { app_id: appId, platform: 'mobile' },
      }));

      return updateBcsAppIdValues(keys, token, bffBaseUrl, bcsEnv);
    })
  );
}